<template>
  <VDataGridV2
    :loader="dataLoader"
    :context-menu="ReceiptsService.contextMenu"
    paginated
    entity-type="receipt"
    @row:dblclick="(row) => openReceiptCard(row)"
  >
    <VDataGridColumnV2
      id="id"
      width="75px"
      label="№"
      sortable
      sort-default-value="desc"
      :filter="$core.filters.idPattern"
    >
      <template #default="{ row }">
        <VLink @click="openReceiptCard(row)">
          {{ $core.utils.string.formatWithDelimiter(row.id) }}
        </VLink>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="$core.currentUser?.isAdmin || $core.currentPartner?.isBuyer"
      id="sellerId"
      label="Грузоотправитель"
      width="250px"
      header-class="nowrap"
      content-class="ellipsis"
      filterable
      filter-type="select"
      filter-send-name="sellerId"
      :filter-props="{
        placeholder:    'Название партнера',
        searchFunction: PartnersService.searchSellers,
        clearable:      true,
      }"
    >
      <template #default="{ row }">
        <div
          class="ellipsis"
          :title="row.shipment?.seller?.name"
        >
          {{ row.shipment?.seller?.name }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="sellerCompanyId"
      :label="$core.currentPartner?.isSeller && !$core.currentUser?.isAdmin ? 'Грузоотправитель' : 'Юр лицо'"
      width="250px"
      header-class="nowrap"
      content-class="ellipsis"
      filterable
      filter-type="select"
      filter-send-name="sellerCompanyId"
      :filter-props="{
        placeholder:    'Введите юр лицо грузоотправителя',
        searchFunction: CompaniesService.searchSellerCompanies,
        clearable:      true,
      }"
    >
      <template #default="{ row }">
        <div
          class="ellipsis"
          :title="row.shipment?.sellerCompany?.name"
        >
          {{ row.shipment?.sellerCompany?.name }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="$core.currentUser?.isAdmin || $core.currentPartner?.isSeller"
      id="buyerId"
      label="Грузополучатель"
      width="250px"
      header-class="nowrap"
      content-class="ellipsis"
      filterable
      filter-type="select"
      filter-send-name="buyerId"
      :filter-props="{
        placeholder:    'Название партнера',
        searchFunction: PartnersService.searchBuyers,
        clearable:      true,
      }"
    >
      <template #default="{ row }">
        <div
          class="ellipsis"
          :title="row.shipment?.buyer?.name"
        >
          {{ row.shipment?.buyer?.name }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="buyerCompanyId"
      :label="$core.currentPartner?.isBuyer && !$core.currentUser?.isAdmin ? 'Грузополучатель' : 'Юр лицо'"
      width="250px"
      header-class="nowrap"
      content-class="ellipsis"
      filterable
      filter-type="select"
      filter-send-name="buyerCompanyId"
      :filter-props="{
        placeholder:    'Введите юр лицо грузополучателя',
        searchFunction: CompaniesService.searchBuyerCompanies,
        clearable:      true,
      }"
    >
      <template #default="{ row }">
        <div
          class="ellipsis"
          :title="row.shipment?.buyerCompany?.name"
        >
          {{ row.shipment?.buyerCompany?.name }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="receiptDate"
      label="Дата приёмки"
      width="130px"
      header-class="nowrap"
      content-align="center"
      content-class="nowrap"
      sortable
      :filter="$core.filters.period('period')"
    >
      <template #default="{ row }">
        {{ $core.utils.date.humanizeDate(row.receiptDate, null) }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="hasComplaints"
      label="Есть претензии"
      width="130px"
      header-class="nowrap"
      content-align="center"
      filterable
      filter-type="select"
      filter-send-name="hasComplaints"
      filter-identity="hasComplaints"
      :filter-props="{
        options: [
          { id: 1, hasComplaints: true,  name: 'Да'  },
          { id: 2, hasComplaints: false, name: 'Нет' },
        ],
      }"
    >
      <template #default="{ row }">
        {{ row.hasComplaints ? 'Да' : 'Нет' }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="isClosed"
      label="Закрыта"
      width="130px"
      header-class="nowrap"
      content-align="center"
      filterable
      filter-type="select"
      filter-send-name="isClosed"
      filter-identity="isClosed"
      :filter-props="{
        options: [
          { id: 1, isClosed: true,  name: 'Да'  },
          { id: 2, isClosed: false, name: 'Нет' },
        ],
      }"
    >
      <template #default="{ row }">
        {{ row.isClosed ? 'Да' : 'Нет' }}
      </template>
    </VDataGridColumnV2>

    <VDataGridActionBarV2>
      <VButton
        theme="primary"
        @click="openReceiptCard(null)"
      >
        Создать приёмку
      </VButton>
    </VDataGridActionBarV2>
  </VDataGridV2>
</template>

<script lang="ts">

import ReceiptsService  from '@/domains/Receipts/ReceiptsService';
import PartnersService  from '@/domains/Partners/PartnersService';
import DataGridLoader   from '@/lib/components/VDataGridV2/DataGridLoader';
import CompaniesService from "@/domains/Companies/CompaniesService";
import Receipt          from "@/domains/Receipts/Receipt";

export default defineComponent({
  name: 'ReceiptsRegistry',

  data() {
    return {
      ReceiptsService,
      PartnersService,
      CompaniesService,

      dataLoader: new DataGridLoader(ReceiptsService.getListPaginated).useExtraParams({
        requireNestedObjects: true
      }),
    };
  },

  methods: {
    async openReceiptCard(receipt: Receipt | null): Promise<void> {
      if (await ReceiptsService.openReceiptCard(receipt)) {
        await this.dataLoader.refresh();
      }
    },
  },
});
</script>
